/**
 * @fileoverview Gallagher Insurance Field Configuration
 *
 * Purpose:
 * - Resolve Morgan canonical/template values into Gallagher portal values.
 * - Build the AJG portal POST payload from the resolved values.
 *
 * Used by:
 * - Generate Precedents wizard
 * - Day One Gallagher portal opener
 * - Convex Gallagher submission action
 */

import { GALLAGHER_INSURANCE_FIELDS } from "@cvx/settings/precedents/gallagherFields";

export type GallagherFieldSourceType = "canonical" | "static" | "blank";

export interface GallagherFieldConfig {
  fieldKey: string;
  sourceType: GallagherFieldSourceType;
  sourceKey?: string;
  defaultValue?: string;
  hidden?: boolean;
}

export interface ResolveGallagherFieldValuesArgs {
  baseValues: Record<string, string>;
  overrides?: Record<string, string>;
  config?: GallagherFieldConfig[];
}

export interface GallagherSourceFieldOption {
  key: string;
  label: string;
}

const LOCATION_STATES = [
  "ACT",
  "NSW",
  "NT",
  "QLD",
  "SA",
  "TAS",
  "VIC",
  "WA",
  "NZ",
] as const;
type LocationState = (typeof LOCATION_STATES)[number];

const LOCATION_STATE_PATTERNS: Record<LocationState, RegExp[]> = {
  ACT: [/\bACT\b/, /\bAUSTRALIAN CAPITAL TERRITORY\b/],
  NSW: [/\bNSW\b/, /\bNEW SOUTH WALES\b/],
  NT: [/\bNT\b/, /\bNORTHERN TERRITORY\b/],
  QLD: [/\bQLD\b/, /\bQUEENSLAND\b/],
  SA: [/\bSA\b/, /\bSOUTH AUSTRALIA\b/],
  TAS: [/\bTAS\b/, /\bTASMANIA\b/],
  VIC: [/\bVIC\b/, /\bVICTORIA\b/],
  WA: [/\bWA\b/, /\bWESTERN AUSTRALIA\b/],
  NZ: [/\bNZ\b/, /\bNEW ZEALAND\b/],
};

export const GALLAGHER_SOURCE_FIELD_OPTIONS: GallagherSourceFieldOption[] = [
  { key: "appointment_date", label: "Appointment date" },
  { key: "firm_name", label: "IP firm" },
  { key: "practitioner_name", label: "Appointee name" },
  { key: "contact_name", label: "Primary contact name" },
  { key: "contact_email", label: "Primary contact email" },
  { key: "firm_phone", label: "Firm phone (switchboard)" },
  { key: "contact_phone", label: "Primary contact phone (direct line)" },
  { key: "mobile_phone", label: "Mobile telephone number" },
  { key: "administrator_type", label: "Type of appointment" },
  // MOR-2342 — the appointment is known by the company on a corporate matter
  // and by the individual's own name on a bankruptcy; the estate heading is
  // available separately for firms that want it.
  {
    key: "appointment_name",
    label: "Appointment name (company, or the bankrupt's name)",
  },
  { key: "company_name", label: "Company name / bankrupt estate heading" },
  { key: "bankrupt_name", label: "Bankrupt name" },
  { key: "acn", label: "Company number" },
  { key: "business_industry", label: "Business / industry" },
  { key: "industry_division", label: "Job setup ANZSIC / industry division" },
  { key: "business_activities", label: "Activities description" },
  { key: "general_comments", label: "General comments" },
  { key: "principal_risk_address", label: "Principal risk address" },
  // MOR-2342 — the full address block (street, suburb, state, postcode), not
  // just the street line the insurer form used to receive.
  {
    key: "registered_office_address",
    label: "Registered office / business address (full)",
  },
  {
    key: "registered_office_street",
    label: "Registered office / business address (street line only)",
  },
  { key: "trading_status", label: "Trading status" },
  { key: "employing_status", label: "Employing status" },
  { key: "locations_by_state", label: "Locations by state" },
  { key: "num_employees", label: "Employees / contractors" },
  { key: "estimated_asset_value", label: "Estimated asset value" },
  {
    key: "estimated_appointment_period",
    label: "Estimated appointment period",
  },
  { key: "estimated_turnover", label: "Estimated turnover" },
  { key: "pre_appointment_insurer", label: "Pre appointment insurer / broker" },
  {
    key: "pre_appointment_workcover",
    label: "Pre appointment WorkCover insurer",
  },
  { key: "crucial_compliance", label: "Crucial compliance requirement" },
  { key: "nominee_1_name", label: "Nominee 1 name" },
  { key: "nominee_1_email", label: "Nominee 1 email" },
  { key: "practitioner_email", label: "Practitioner email" },
  { key: "nominee_2_name", label: "Nominee 2 name" },
  { key: "nominee_2_email", label: "Nominee 2 email" },
];

export const GALLAGHER_FIELD_KEYS = new Set(
  GALLAGHER_INSURANCE_FIELDS.map((field) => field.key),
);

export const GALLAGHER_SOURCE_FIELD_KEYS = new Set(
  GALLAGHER_SOURCE_FIELD_OPTIONS.map((option) => option.key),
);

const DEFAULT_GALLAGHER_CONFIG_OVERRIDES: Record<string, GallagherFieldConfig> =
  {
    business_industry: {
      fieldKey: "business_industry",
      sourceType: "canonical",
      sourceKey: "industry_division",
    },
    business_activities: {
      fieldKey: "business_activities",
      sourceType: "static",
      defaultValue: "no further details known",
    },
    general_comments: {
      fieldKey: "general_comments",
      sourceType: "blank",
    },
    principal_risk_address: {
      fieldKey: "principal_risk_address",
      sourceType: "canonical",
      sourceKey: "registered_office_address",
    },
    // MOR-2342 — "6. Appointment name" is the bankrupt's own name on a
    // personal insolvency (the company name otherwise), never the
    // "Bankrupt Estate of …" heading.
    company_name: {
      fieldKey: "company_name",
      sourceType: "canonical",
      sourceKey: "appointment_name",
    },
    // MOR-2342 — "4b. Direct telephone number" is the primary contact's own
    // line. `contact_phone` falls back to `firm_phone` in `mergeFieldValues`
    // when the firm has not configured a contact person.
    firm_phone: {
      fieldKey: "firm_phone",
      sourceType: "canonical",
      sourceKey: "contact_phone",
    },
  };

/**
 * Saved portal-field configs written by the Portal Fields editor before
 * MOR-2342 carry every row, including untouched rows that simply mirrored the
 * defaults of the day (`registered_office_street`, `company_name`,
 * `firm_phone`). Those exact rows are what the customer reported as wrong, so
 * treat them as "still on the default" and upgrade them to the current
 * default source. A row the firm deliberately changed (a different source,
 * static text, blank, or hidden) is left alone.
 */
const LEGACY_DEFAULT_SOURCE_UPGRADES: Record<
  string,
  { from: string; to: string }
> = {
  principal_risk_address: {
    from: "registered_office_street",
    to: "registered_office_address",
  },
  company_name: { from: "company_name", to: "appointment_name" },
  firm_phone: { from: "firm_phone", to: "contact_phone" },
};

/**
 * Upgrade a saved config row that still mirrors a pre-MOR-2342 default source
 * to the current default. Returns the row unchanged otherwise.
 *
 * @example
 * upgradeLegacyGallagherSource({ fieldKey: "company_name", sourceType: "canonical", sourceKey: "company_name" }).sourceKey; // "appointment_name"
 */
export function upgradeLegacyGallagherSource(
  entry: GallagherFieldConfig,
): GallagherFieldConfig {
  const upgrade = LEGACY_DEFAULT_SOURCE_UPGRADES[entry.fieldKey];
  if (
    !upgrade ||
    entry.sourceType !== "canonical" ||
    entry.sourceKey !== upgrade.from ||
    entry.hidden
  ) {
    return entry;
  }
  return { ...entry, sourceKey: upgrade.to };
}

export const DEFAULT_GALLAGHER_FIELD_CONFIG: GallagherFieldConfig[] =
  GALLAGHER_INSURANCE_FIELDS.map((field) => {
    const override = DEFAULT_GALLAGHER_CONFIG_OVERRIDES[field.key];
    if (override) return override;
    return {
      fieldKey: field.key,
      sourceType: "canonical",
      sourceKey: field.canonicalKey ?? field.key,
    };
  });

/**
 * Gallagher field keys that are OUTPUT names only (MOR-2342): the field's
 * default source is a different canonical key, so a value saved under the
 * field key on a generated Gallagher document (e.g. `firm_phone` holding the
 * resolved direct line, `company_name` holding the appointment name) is a
 * resolved result, not an analyst's input. Rehydrating such a value as an
 * override would silently outrank the reviewed canonical field on the next
 * generation, or corrupt the same-named canonical field for every document.
 */
export const GALLAGHER_OUTPUT_ONLY_KEYS: ReadonlySet<string> = new Set(
  DEFAULT_GALLAGHER_FIELD_CONFIG.filter(
    (entry) =>
      entry.sourceType === "canonical" &&
      entry.sourceKey !== undefined &&
      entry.sourceKey !== entry.fieldKey,
  ).map((entry) => entry.fieldKey),
);

/**
 * Applies Gallagher template configuration to canonical/job/default values.
 *
 * Precedence per field (MOR-2342):
 *   1. an override of the field's configured canonical SOURCE key — this is
 *      what the wizard's review grid shows and edits (e.g. `contact_phone`
 *      for "4b. Direct telephone number", `appointment_name` for
 *      "6. Appointment name"); an explicit "" is a deliberate blank;
 *   2. an override under the Gallagher field key itself — a value saved on
 *      this document (the Day One preview's Edit Fields);
 *   3. the configured source / static default.
 * Hidden fields are removed entirely so they cannot appear in the Day One
 * table or AJG POST.
 */
export function resolveGallagherFieldValues({
  baseValues,
  overrides = {},
  config,
}: ResolveGallagherFieldValuesArgs): Record<string, string> {
  const configByField = buildConfigMap(config);
  const hiddenFields = new Set(
    [...configByField.entries()]
      .filter(([, entry]) => entry.hidden)
      .map(([fieldKey]) => fieldKey),
  );
  const resolved: Record<string, string> = {};

  for (const field of GALLAGHER_INSURANCE_FIELDS) {
    if (hiddenFields.has(field.key)) continue;

    const configured = configByField.get(field.key);
    const sourceKey =
      configured?.sourceType === "canonical" &&
      configured.sourceKey &&
      configured.sourceKey !== field.key
        ? configured.sourceKey
        : undefined;
    const sourceOverride = sourceKey ? overrides[sourceKey] : undefined;
    const override = overrides[field.key];
    const value =
      sourceOverride !== undefined
        ? sourceOverride
        : override !== undefined
          ? override
          : resolveConfiguredValue(configured, baseValues, field.key);

    if (value !== undefined && value !== "") {
      resolved[field.key] = value;
    }
  }

  if (
    !hiddenFields.has("nominee_1_email") &&
    !resolved.nominee_1_email &&
    (overrides.nominee_1_email ??
      baseValues.nominee_1_email ??
      baseValues.practitioner_email)
  ) {
    resolved.nominee_1_email =
      overrides.nominee_1_email ??
      baseValues.nominee_1_email ??
      baseValues.practitioner_email;
  }

  return resolved;
}

/**
 * Builds the AJG portal payload using Gallagher's HTML field names.
 * Empty values are omitted except for state checkbox fields, which Gallagher
 * expects as explicit true/false values.
 */
export function buildGallagherPortalPayload(
  values: Record<string, string>,
): Record<string, string> {
  const payload = compactPayload({
    DateOfAppointment: formatGallagherDate(
      valueFor(values, "appointment_date"),
    ),
    InsolvencyPractitioner: valueFor(values, "firm_name"),
    NameOfAppointees: valueFor(values, "practitioner_name"),
    PrimaryContact: valueFor(values, "contact_name"),
    PrimaryContactEmail: valueFor(values, "contact_email"),
    PrimaryContactPhone: valueFor(values, "firm_phone"),
    PrimaryContactMobile: valueFor(values, "mobile_phone"),
    TypeOfAppointment: valueFor(values, "administrator_type"),
    AppointmentName: valueFor(values, "company_name"),
    BusinessRegistration: valueFor(values, "acn"),
    OccupationOfAppointment: valueFor(
      values,
      "business_industry",
      "industry_division",
    ),
    OccupationDescription: valueFor(values, "business_activities"),
    GeneralComments: valueFor(values, "general_comments"),
    PrincipalRiskAddress: valueFor(
      values,
      "principal_risk_address",
      "registered_office_address",
      "registered_office_street",
    ),
    TradingStatusId: mapTradingStatus(valueFor(values, "trading_status")),
    EmployingStatusId: mapEmployingStatus(valueFor(values, "employing_status")),
    NumberOfEmployees: valueFor(values, "num_employees"),
    EstimatedAssetValue: valueFor(values, "estimated_asset_value"),
    EstimatedAppointmentPeriod: valueFor(
      values,
      "estimated_appointment_period",
    ),
    EstimatedTurnoverDuringAppointment: valueFor(values, "estimated_turnover"),
    PreAppointmentInsurerBroker: valueFor(values, "pre_appointment_insurer"),
    PreAppointmentWorkCoverInsurer: valueFor(
      values,
      "pre_appointment_workcover",
    ),
    CrucialCompliance: mapYesNo(valueFor(values, "crucial_compliance")),
    "Nominee1.Name": valueFor(values, "nominee_1_name"),
    "Nominee1.Email": valueFor(values, "nominee_1_email"),
    "Nominee2.Name": valueFor(values, "nominee_2_name"),
    "Nominee2.Email": valueFor(values, "nominee_2_email"),
  });

  const selectedLocations = resolveLocationStates(values);
  for (const state of LOCATION_STATES) {
    payload[`chkSolicitation${state}`] = selectedLocations.includes(state)
      ? "true"
      : "false";
  }
  if (selectedLocations.length > 0) {
    payload.Locations = `${selectedLocations.join(";")};`;
  }

  return payload;
}

function buildConfigMap(
  config: GallagherFieldConfig[] | undefined,
): Map<string, GallagherFieldConfig> {
  const byField = new Map<string, GallagherFieldConfig>();
  for (const entry of DEFAULT_GALLAGHER_FIELD_CONFIG) {
    byField.set(entry.fieldKey, entry);
  }
  for (const saved of config ?? []) {
    if (!GALLAGHER_FIELD_KEYS.has(saved.fieldKey)) continue;
    const entry = upgradeLegacyGallagherSource(saved);
    if (
      entry.sourceType === "canonical" &&
      (!entry.sourceKey || !GALLAGHER_SOURCE_FIELD_KEYS.has(entry.sourceKey))
    ) {
      continue;
    }
    byField.set(entry.fieldKey, entry);
  }
  return byField;
}

/**
 * Resolves the value for a single Gallagher field from its configured source.
 *
 * For canonical sources we additionally fall back to a value already saved
 * directly under the field key. This matters in the Day One preview, where
 * the analyst may edit a Gallagher field directly via Edit Fields when the
 * canonical source is empty.
 *
 * The configured source still wins when it is populated, so wizard
 * regenerations continue to refresh from job-derived data (e.g. the ANZSIC
 * division).
 */
function resolveConfiguredValue(
  config: GallagherFieldConfig | undefined,
  baseValues: Record<string, string>,
  fieldKey: string,
): string | undefined {
  const sourceType = config?.sourceType ?? "canonical";
  switch (sourceType) {
    case "canonical": {
      const sourceKey = config?.sourceKey;
      const sourceValue = sourceKey ? baseValues[sourceKey] : undefined;
      // An explicitly blank source (the analyst cleared the field) is a
      // deliberate blank — it must not fall through to a same-named firm
      // value such as the switchboard number (MOR-2342 QA-08).
      if (sourceValue !== undefined) return sourceValue;
      const direct = baseValues[fieldKey];
      if (direct !== undefined && direct !== "") return direct;
      // A field on the current default source still honours the value its
      // pre-MOR-2342 source would have produced (e.g. a street-only legacy
      // address), so older saved values are never lost to the upgrade.
      const legacy = LEGACY_DEFAULT_SOURCE_UPGRADES[fieldKey];
      const legacyValue =
        legacy && sourceKey === legacy.to ? baseValues[legacy.from] : undefined;
      return legacyValue !== undefined && legacyValue !== ""
        ? legacyValue
        : undefined;
    }
    case "static":
      return config?.defaultValue;
    case "blank":
      return undefined;
    default: {
      const exhaustive: never = sourceType;
      return exhaustive;
    }
  }
}

function valueFor(
  values: Record<string, string>,
  primaryKey: string,
  ...fallbackKeys: string[]
): string {
  const found = [primaryKey, ...fallbackKeys]
    .map((key) => values[key])
    .find((value) => value !== undefined && value !== "");
  return found ?? "";
}

function compactPayload(
  values: Record<string, string>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== ""),
  );
}

function resolveLocationStates(
  values: Record<string, string>,
): LocationState[] {
  const locationSource =
    valueFor(values, "locations_by_state") ||
    valueFor(
      values,
      "principal_risk_address",
      "registered_office_address",
      "registered_office_street",
    );
  const normalized = locationSource.toUpperCase();

  return LOCATION_STATES.filter((state) =>
    LOCATION_STATE_PATTERNS[state].some((pattern) => pattern.test(normalized)),
  );
}

function formatGallagherDate(value: string): string {
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!isoMatch) return value;
  return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
}

function mapTradingStatus(status: string): string {
  const normalized = status.trim().toLowerCase();
  if (!normalized) return "";
  if (normalized.includes("not trading") || normalized.includes("ceased"))
    return "2";
  if (normalized.includes("not applicable") || normalized === "n/a") return "3";
  if (normalized.includes("unknown")) return "4";
  if (normalized.includes("trading")) return "1";
  return "";
}

function mapEmployingStatus(status: string): string {
  const normalized = status.trim().toLowerCase();
  if (!normalized) return "";
  if (normalized.includes("not employing")) return "2";
  if (normalized.includes("not applicable") || normalized === "n/a") return "3";
  if (normalized.includes("unknown")) return "4";
  if (normalized.includes("employing")) return "1";
  return "";
}

function mapYesNo(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return "";
  return normalized === "yes" ||
    normalized.startsWith("y") ||
    normalized === "true"
    ? "True"
    : "False";
}
